import React from 'react';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field, formValueSelector } from 'redux-form';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
// data
import onEventFaqSubmit from './data/onEventFaqSubmit';
import { eventFaqDefinition } from '../shared-components/faq/data/eventFaqDefinition';
import { hasQuantityAndDescription, mapBoolToString, pageInfo } from './data/utils';
import { onEventFaqBlur, onEventFaqChange, onEventMiscItemsChange } from './data/onEventFaqChange';
import { EVENT_FAQ_CONST, ALCOHOLIC_BEVERAGE_FORM, EVENT_FAQ_ANSWERS_CONST } from '../shared-components/faq/data/constants';
// faq component
import FaqHeader from '../app-faq-components/FaqHeader';
import ToggleFieldRow from '../app-faq-components/ToggleFieldRow';
import ButtonNavigation from '../app-faq-components/ButtonNavigation';
import RadioField from '../app-main/components/reduxForms/fields/RadioField/RadioField';
// shared component
import TextAreaField from '../shared-components/reduxForms/fields/TextAreaField/TextAreaField';
const {
  BRING_ALCOHOL,
  BRING_ALCOHOL_RULES,
  ALCOHOL_MUST_BE_SERVED_BY_LICENSED_VENDOR,
  CAN_PURCHASE_ALCOHOL_AT_FULL_SERVICE_BAR,
  CAN_PURCHASE_BEER_WINE_AT_BAR,
  OTHER_ALCOHOL_CONSUMPTION_RULES
} = EVENT_FAQ_CONST;
const {
  BRING_ALCOHOL_ALLOWED,
  BRING_ALCOHOL_NOT_ALLOWED,
  NO_ALCOHOL_ALLOWED,
  ANY_ALCOHOL,
  LIQUOR_NOT_ALLOWED,
  LICENSED_LIQUOR
} = EVENT_FAQ_ANSWERS_CONST;
const AlcoholicBeverageForm = ({
  error,
  handleSubmit,
  intl,
  page,
  hasOtherAlcoholRulesSelected,
  bringAlcoholAllowed,
  bringAlcoholNotAllowed,
  spaceId,
  accessToken,
  showErrorToast,
  ...props
}) => {
  const alcoholBeverages = get(eventFaqDefinition, 'alcohol_beverages', {});
  const header = intl.formatMessage(pageInfo(page).header);
  const Icon = pageInfo(page).icon;
  const onChangeFn = onEventFaqChange(props.dispatch, spaceId, accessToken, showErrorToast);
  const onBlurFn = onEventFaqBlur(props.dispatch, spaceId, accessToken, showErrorToast);
  const onChangeMiscFn = onEventMiscItemsChange(props.dispatch, spaceId, accessToken, showErrorToast);

  return (
    <form className="flex-basis-100" onSubmit={handleSubmit}>
      <FaqHeader header={header} icon={<Icon />} />
      <div>
        <div className="h6 questions-section">
          <FormattedMessage
            id="hosting.alcoholic_beverage_form.alcohol_rules_header"
            defaultMessage="Rules regarding guests and alcohol"
          />
        </div>
        <Field
          component={RadioField}
          name={BRING_ALCOHOL}
          className="flex baseline padding-xl-bottom margin-lg-top flex-wrap space-between border-bottom-gray-200"
          onChange={onChangeFn}
          options={[
            BRING_ALCOHOL_ALLOWED,
            BRING_ALCOHOL_NOT_ALLOWED,
            NO_ALCOHOL_ALLOWED
          ].map((item) => (
            {
              id: item,
              value: item,
              label: intl.formatMessage(alcoholBeverages[BRING_ALCOHOL][item])
            })
          )}
        />
      </div>
      { bringAlcoholAllowed &&
        <div>
          <div className="h6 questions-section">
            <FormattedMessage
              id="hosting.alcoholic_beverage_form.bring_alcohol_rules_header"
              defaultMessage="Alcohol that guests are allowed to bring"
            />
          </div>
          <Field
            component={RadioField}
            name={BRING_ALCOHOL_RULES}
            className="flex baseline padding-xl-bottom margin-lg-top flex-wrap space-between border-bottom-gray-200"
            onChange={onChangeFn}
            options={[
              ANY_ALCOHOL,
              LIQUOR_NOT_ALLOWED,
              LICENSED_LIQUOR
            ].map((item) => (
              {
                id: item,
                value: item,
                label: intl.formatMessage(alcoholBeverages[BRING_ALCOHOL_RULES][item])
              }
            ))}
          />
        </div>
      }
      { bringAlcoholNotAllowed &&
        <div>
          <div className="h6 questions-section">
            <FormattedMessage
              id="hosting.alcoholic_beverage_form.alcohol_consumption_rules_header"
              defaultMessage="Available options for alcohol consumption"
            />
          </div>
          {
            [ALCOHOL_MUST_BE_SERVED_BY_LICENSED_VENDOR,
              CAN_PURCHASE_ALCOHOL_AT_FULL_SERVICE_BAR,
              CAN_PURCHASE_BEER_WINE_AT_BAR
            ].map((key) => (
              <>
                <ToggleFieldRow
                  label={alcoholBeverages.alcohol_consumption_options[key]}
                  formName={ALCOHOLIC_BEVERAGE_FORM}
                  fieldName={key}
                  onChange={onChangeFn}
                  dispatch={props.dispatch}
                />
                <hr />
              </>
            ))
          }
          <ToggleFieldRow
            label={alcoholBeverages.alcohol_consumption_options[OTHER_ALCOHOL_CONSUMPTION_RULES]}
            formName={ALCOHOLIC_BEVERAGE_FORM}
            fieldName={`${OTHER_ALCOHOL_CONSUMPTION_RULES}_toggle`}
            onChange={onChangeMiscFn}
            dispatch={props.dispatch}
          />
          {
            hasOtherAlcoholRulesSelected &&
            <div className="margin-xl-top">
              <Field
                name={OTHER_ALCOHOL_CONSUMPTION_RULES}
                component={TextAreaField}
                attributes={{
                  'data-testing-id': OTHER_ALCOHOL_CONSUMPTION_RULES
                }}
                onBlur={onBlurFn}
                placeholder={intl.formatMessage(alcoholBeverages.alcohol_consumption_options[OTHER_ALCOHOL_CONSUMPTION_RULES].placeholder)}
                maxLength={1000}
              />
            </div>
          }
          <hr />
        </div>
      }
      <div className="text-danger text-small margin-xl-bottom">{error}</div>
      <ButtonNavigation {...props} spaceId={spaceId} />
    </form>
  );
};

AlcoholicBeverageForm.propTypes = {
  handleSubmit: PropTypes.func,
  error: PropTypes.string,
  dispatch: PropTypes.func,
  intl: intlShape,
  hasOtherAlcoholRulesSelected: PropTypes.bool,
  bringAlcoholAllowed: PropTypes.bool,
  bringAlcoholNotAllowed: PropTypes.bool,
  spaceId: PropTypes.string,
  accessToken: PropTypes.string,
  showErrorToast: PropTypes.bool
};

const config = {
  form: ALCOHOLIC_BEVERAGE_FORM,
  onSubmit: onEventFaqSubmit,
};

const selector = formValueSelector(ALCOHOLIC_BEVERAGE_FORM);

function mapStateToProps (state, { eventFaq }) {
  const alcoholAvailabilitiesField = [
    ALCOHOL_MUST_BE_SERVED_BY_LICENSED_VENDOR,
    CAN_PURCHASE_ALCOHOL_AT_FULL_SERVICE_BAR,
    CAN_PURCHASE_BEER_WINE_AT_BAR,
    OTHER_ALCOHOL_CONSUMPTION_RULES
  ];
  const initialValues = {
    ...mapBoolToString(eventFaq),
    [`${OTHER_ALCOHOL_CONSUMPTION_RULES}_toggle`]: !!get(eventFaq, OTHER_ALCOHOL_CONSUMPTION_RULES) ? 'true' : 'false'
  };
  const itemSelected = hasQuantityAndDescription(alcoholAvailabilitiesField, selector, state);
  const bringAlcoholAllowed = selector(state, BRING_ALCOHOL) === BRING_ALCOHOL_ALLOWED;
  const bringAlcoholNotAllowed = selector(state, BRING_ALCOHOL) === BRING_ALCOHOL_NOT_ALLOWED;
  const hasOtherAlcoholRulesSelected = selector(state, `${OTHER_ALCOHOL_CONSUMPTION_RULES}_toggle`) === 'true';
  return {
    bringAlcoholAllowed,
    bringAlcoholNotAllowed,
    hasOtherAlcoholRulesSelected,
    ...itemSelected,
    initialValues
  };
};

export default connect(mapStateToProps)(reduxForm(config)(injectIntl(AlcoholicBeverageForm)));
